/**
 * Notes Page - Notion-style documentation
 *
 * Features:
 * - Note list with icons and pinned notes
 * - Rich note viewer
 * - Quick note creation
 * - Tags and search
 */

import { useState, FormEvent } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  getNotesByWorkspace,
  getPinnedNotes,
  createNote,
  pinNote,
  unpinNote,
  archiveNote,
  deleteNote,
  getNoteTags,
  getNotesByTag,
} from '../lib/services/notes.service';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  XMarkIcon,
  DocumentTextIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import clsx from 'clsx';
import type { Note } from '../lib/types';

const EMOJI_OPTIONS = ['📝', '📋', '💡', '🎯', '📊', '🔧', '📚', '🚀', '💼', '🏢'];

export function NotesPage() {
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  if (!currentWorkspace) return null;

  const allNotes = getNotesByWorkspace(currentWorkspace.id);
  const pinnedNotes = getPinnedNotes(currentWorkspace.id);
  const tags = getNoteTags(currentWorkspace.id);

  // Filter notes
  let filteredNotes = allNotes;
  if (selectedTag) {
    filteredNotes = getNotesByTag(currentWorkspace.id, selectedTag);
  }
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredNotes = filteredNotes.filter(
      (note) =>
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query)
    );
  }

  // Separate pinned and unpinned
  const unpinnedNotes = filteredNotes.filter((n) => !n.isPinned);

  const handleCreateNote = (data: { title: string; content: string; icon?: string }) => {
    createNote({
      workspaceId: currentWorkspace.id,
      ...data,
    });
    setShowAddForm(false);
    refreshWorkspaces();
  };

  const handlePinToggle = (noteId: string, isPinned: boolean) => {
    if (isPinned) {
      unpinNote(noteId);
    } else {
      pinNote(noteId);
    }
    refreshWorkspaces();
  };

  const handleArchive = (noteId: string) => {
    archiveNote(noteId);
    setSelectedNote(null);
    refreshWorkspaces();
  };

  const handleDelete = (noteId: string) => {
    deleteNote(noteId);
    setSelectedNote(null);
    refreshWorkspaces();
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex">
      {/* Sidebar */}
      <div className="w-72 border-r border-gray-200 bg-white flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
            <button onClick={() => setShowAddForm(true)} className="btn-icon">
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes..."
              className="input pl-9 py-1.5 text-sm"
            />
          </div>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-500 mb-2">Tags</p>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedTag(null)}
                className={clsx(
                  'text-xs px-2 py-1 rounded-full transition-colors',
                  !selectedTag ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                All
              </button>
              {tags.slice(0, 8).map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  className={clsx(
                    'text-xs px-2 py-1 rounded-full transition-colors',
                    selectedTag === tag ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes list */}
        <div className="flex-1 overflow-y-auto">
          {/* Pinned notes */}
          {pinnedNotes.length > 0 && !selectedTag && !searchQuery && (
            <div className="p-2">
              <p className="text-xs font-medium text-gray-500 px-2 py-1">Pinned</p>
              {pinnedNotes.map((note) => (
                <NoteListItem
                  key={note.id}
                  note={note}
                  isSelected={selectedNote?.id === note.id}
                  onSelect={() => setSelectedNote(note)}
                />
              ))}
            </div>
          )}

          {/* All notes */}
          <div className="p-2">
            {(!selectedTag && !searchQuery && pinnedNotes.length > 0) && (
              <p className="text-xs font-medium text-gray-500 px-2 py-1">All Notes</p>
            )}
            {unpinnedNotes.map((note) => (
              <NoteListItem
                key={note.id}
                note={note}
                isSelected={selectedNote?.id === note.id}
                onSelect={() => setSelectedNote(note)}
              />
            ))}
            {filteredNotes.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-400">
                {searchQuery ? 'No matching notes' : 'No notes yet'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 bg-gray-50">
        {selectedNote ? (
          <NoteViewer
            note={selectedNote}
            onPinToggle={() => handlePinToggle(selectedNote.id, selectedNote.isPinned || false)}
            onArchive={() => handleArchive(selectedNote.id)}
            onDelete={() => handleDelete(selectedNote.id)}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Select a note</h3>
              <p className="text-sm text-gray-500 mb-4">Choose a note from the sidebar or create a new one</p>
              <button onClick={() => setShowAddForm(true)} className="btn-primary btn-sm">
                <PlusIcon className="h-4 w-4" />
                New Note
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Note Modal */}
      {showAddForm && (
        <AddNoteModal
          onClose={() => setShowAddForm(false)}
          onSubmit={handleCreateNote}
        />
      )}
    </div>
  );
}

function NoteListItem({
  note,
  isSelected,
  onSelect,
}: {
  note: Note;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={clsx(
        'w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors',
        isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-100'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg flex-shrink-0">{note.icon || '📝'}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-medium text-gray-900 truncate">{note.title}</h3>
            {note.isPinned && (
              <StarIcon className="h-3 w-3 text-amber-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {format(new Date(note.updatedAt), 'MMM d')}
          </p>
        </div>
      </div>
    </button>
  );
}

function NoteViewer({
  note,
  onPinToggle,
  onArchive,
  onDelete,
}: {
  note: Note;
  onPinToggle: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{note.icon || '📝'}</span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{note.title}</h1>
            <p className="text-xs text-gray-500">
              Last edited {format(new Date(note.updatedAt), 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (confirm(`Delete note "${note.title}"?`)) {
                onDelete();
              }
            }}
            className="btn-icon hover:text-red-500"
            title="Delete"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <button
            onClick={onPinToggle}
            className={clsx(
              'btn-icon',
              note.isPinned && 'text-amber-500 hover:text-amber-600'
            )}
            title={note.isPinned ? 'Unpin' : 'Pin'}
          >
            {note.isPinned ? (
              <StarIcon className="h-5 w-5" />
            ) : (
              <PinIcon className="h-5 w-5" />
            )}
          </button>
          <button onClick={onArchive} className="btn-icon" title="Archive">
            <ArchiveBoxIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          {/* Tags */}
          {note.tags.length > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <HashtagIcon className="h-4 w-4 text-gray-400" />
              <div className="flex flex-wrap gap-1">
                {note.tags.map((tag) => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Content with markdown-like rendering */}
          <div className="prose-sm">
            {note.content.split('\n').map((line, i) => {
              // Simple markdown parsing
              if (line.startsWith('# ')) {
                return <h1 key={i}>{line.slice(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={i}>{line.slice(3)}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={i}>{line.slice(4)}</h3>;
              }
              if (line.startsWith('- ')) {
                return <li key={i}>{line.slice(2)}</li>;
              }
              if (line.startsWith('- [ ] ')) {
                return (
                  <div key={i} className="flex items-center gap-2">
                    <input type="checkbox" className="checkbox" disabled />
                    <span>{line.slice(6)}</span>
                  </div>
                );
              }
              if (line.startsWith('- [x] ')) {
                return (
                  <div key={i} className="flex items-center gap-2">
                    <input type="checkbox" className="checkbox" checked disabled />
                    <span className="line-through text-gray-400">{line.slice(6)}</span>
                  </div>
                );
              }
              if (line.startsWith('---')) {
                return <hr key={i} />;
              }
              if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={i} className="italic text-gray-500">{line.slice(1, -1)}</p>;
              }
              if (line.startsWith('|')) {
                // Table row - simple rendering
                return (
                  <div key={i} className="font-mono text-sm">
                    {line}
                  </div>
                );
              }
              if (line.trim() === '') {
                return <br key={i} />;
              }
              return <p key={i}>{line}</p>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddNoteModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: { title: string; content: string; icon?: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [icon, setIcon] = useState('📝');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      content: content.trim(),
      icon,
    });
  };

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content animate-slide-in max-w-xl">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-gray-900">Create New Note</h2>
          <button onClick={onClose} className="btn-icon">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div>
              <label className="label">Icon</label>
              <div className="flex gap-2">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setIcon(emoji)}
                    className={clsx(
                      'w-10 h-10 text-xl rounded-lg transition-colors',
                      icon === emoji ? 'bg-primary-100 border-2 border-primary-500' : 'bg-gray-100 hover:bg-gray-200'
                    )}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="Note title"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="textarea font-mono text-sm"
                placeholder="Write your note... (supports markdown)"
                rows={10}
              />
              <p className="text-xs text-gray-400 mt-1">
                Supports markdown: # Heading, ## Subheading, - List, - [ ] Checkbox
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!title.trim()}>
              Create Note
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Heroicons doesn't have PinIcon, using a simple one
function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.75V7.5M7.5 3.75v3.75m9-3.75H7.5m9 0a3 3 0 013 3v1.5a.75.75 0 01-.75.75h-2.25m-9 0a3 3 0 00-3 3v1.5c0 .414.336.75.75.75h2.25m9 0h-9m9 0v6.75a3 3 0 01-3 3H9a3 3 0 01-3-3v-6.75" />
    </svg>
  );
}

export default NotesPage;
